import { create } from "zustand";
import { executeAPIRequest } from "@/utils/api";
import env from "@/env";

export type SupplierDashboardStats = {
  totalIoEarned: number;
  activeDevices: number;
  offlineDevices: number;
  totalDevices: number;
  epochsParticipated: number;
  totalEpochs: number;
  openDebtOwed: number;
  lastUpdated: string;
};

export type SupplierWeeklyEarning = {
  day: string;
  amount: number;
};

export type SupplierDashboardData = {
  supplierId: string;
  stats: SupplierDashboardStats;
  weeklyEarnings: SupplierWeeklyEarning[];
  lastWeekEarnings: SupplierWeeklyEarning[];
};

export type SupplierDevice = {
  status: string;
  deviceId: string;
  model: string;
  vram: number | null;
  uptime30d: number | null;
  ioEarnedAllTime: number;
};

export type SupplierEpochHistoryItem = {
  epochId: string;
  start: string;
  end: string;
  devicesActive: number;
  totalPayout: number;
  epochSignature: string | null;
};

export type SupplierEpochHistoryData = {
  total: number;
  page: number;
  pageSize: number;
  epochs: SupplierEpochHistoryItem[];
};

type SupplierPaginationRequest = {
  supplierId?: string;
  page: number;
  limit: number;
};

type UseSupplierStore = {
  dashboardData?: SupplierDashboardData;
  devices: SupplierDevice[];
  epochHistory: SupplierEpochHistoryData;
  isDashboardLoading: boolean;
  isDevicesLoading: boolean;
  isEpochHistoryLoading: boolean;
  fetchSupplierDashboard: () => Promise<void>;
  fetchSupplierDevices: () => Promise<void>;
  fetchSupplierEpochHistory: (page: number, pageSize: number) => Promise<void>;
  fetchSupplierDevicesRequest: (
    options: SupplierPaginationRequest
  ) => Promise<{ results: SupplierDevice[]; resultCount: number }>;
  fetchSupplierEpochHistoryRequest: (
    options: SupplierPaginationRequest
  ) => Promise<{ results: SupplierEpochHistoryItem[]; resultCount: number }>;
};

type SupplierDashboardResponse = {
  status: string;
  data: {
    supplier: {
      supplier_id: string;
    };
    stats: {
      total_io_earned: number;
      active_devices: number;
      active_gpus: number;
      offline_devices: number;
      total_devices: number;
      epochs_participated: number;
      total_epochs: number;
      open_debt_owed: number;
      last_updated: string;
    };
    weekly_earnings: {
      this_week_earnings: Array<{
        day: string;
        amount: number;
      }>;
      last_week_earnings?: Array<{
        day: string;
        amount: number;
      }>;
    };
  };
};

type SupplierDevicesResponse = {
  status: string;
  data: {
    devices: Array<{
      status: string;
      device_id: string;
      model: string;
      vram?: number | null;
      uptime_30d?: number | null;
      uptime?: number | null;
      io_earned_all_time: number;
    }>;
  };
};

type SupplierEpochHistoryResponse = {
  status: string;
  data: {
    total: number;
    epochs: Array<{
      epoch_id: string;
      start: string;
      end: string;
      devices_active: number;
      total_payout: number;
      epoch_signature?: string | null;
    }>;
  };
};

const SUPPLIER_TOKENOMICS_BASE_URL = `${env.API_URL}/io-explorer/tokenomics/suppliers`;

export const useSupplierStore = create<UseSupplierStore>((set) => ({
  dashboardData: undefined,
  devices: [],
  epochHistory: {
    total: 0,
    page: 1,
    pageSize: 10,
    epochs: []
  },
  isDashboardLoading: false,
  isDevicesLoading: false,
  isEpochHistoryLoading: false,
  fetchSupplierDashboard: async () => {
    set({ isDashboardLoading: true });
    try {
      const response = await executeAPIRequest<SupplierDashboardResponse>({
        method: "get",
        url: `${SUPPLIER_TOKENOMICS_BASE_URL}/me`,
        options: {
          replaceUrl: true
        }
      });

      set({
        dashboardData: {
          supplierId: response.data.supplier.supplier_id,
          stats: {
            totalIoEarned: response.data.stats.total_io_earned,
            activeDevices: response.data.stats.active_devices,
            offlineDevices: response.data.stats.offline_devices,
            totalDevices: response.data.stats.total_devices,
            epochsParticipated: response.data.stats.epochs_participated,
            totalEpochs: response.data.stats.total_epochs,
            openDebtOwed: response.data.stats.open_debt_owed,
            lastUpdated: response.data.stats.last_updated
          },
          weeklyEarnings: response.data.weekly_earnings.this_week_earnings ?? [],
          lastWeekEarnings: response.data.weekly_earnings.last_week_earnings ?? []
        }
      });
    } catch (error) {
      set({ dashboardData: undefined });
      throw error;
    } finally {
      set({ isDashboardLoading: false });
    }
  },
  fetchSupplierDevices: async () => {
    set({ isDevicesLoading: true });
    try {
      const response = await executeAPIRequest<SupplierDevicesResponse>({
        method: "get",
        url: `${SUPPLIER_TOKENOMICS_BASE_URL}/me/devices`,
        options: {
          replaceUrl: true
        }
      });

      set({
        devices: (response.data.devices ?? []).map((device) => ({
          status: device.status,
          deviceId: device.device_id,
          model: device.model,
          vram: typeof device.vram === "number" ? device.vram : null,
          uptime30d:
            typeof device.uptime_30d === "number"
              ? device.uptime_30d
              : typeof device.uptime === "number"
                ? device.uptime
                : null,
          ioEarnedAllTime: device.io_earned_all_time
        }))
      });
    } catch (error) {
      set({ devices: [] });
      throw error;
    } finally {
      set({ isDevicesLoading: false });
    }
  },
  fetchSupplierEpochHistory: async (page: number, pageSize: number) => {
    set({ isEpochHistoryLoading: true });
    try {
      const response = await executeAPIRequest<SupplierEpochHistoryResponse>({
        method: "get",
        url: `${SUPPLIER_TOKENOMICS_BASE_URL}/me/epochs?page=${page}&page_size=${pageSize}`,
        options: {
          replaceUrl: true
        }
      });

      set({
        epochHistory: {
          total: response.data.total ?? 0,
          page,
          pageSize,
          epochs: (response.data.epochs ?? []).map((epoch) => ({
            epochId: epoch.epoch_id,
            start: epoch.start,
            end: epoch.end,
            devicesActive: epoch.devices_active,
            totalPayout: epoch.total_payout,
            epochSignature: typeof epoch.epoch_signature === "string" ? epoch.epoch_signature : null
          }))
        }
      });
    } catch (error) {
      set({
        epochHistory: {
          total: 0,
          page,
          pageSize,
          epochs: []
        }
      });
      throw error;
    } finally {
      set({ isEpochHistoryLoading: false });
    }
  },
  fetchSupplierDevicesRequest: async ({ page, limit }) => {
    try {
      const response = await executeAPIRequest<SupplierDevicesResponse>({
        method: "get",
        url: `${SUPPLIER_TOKENOMICS_BASE_URL}/me/devices`,
        options: {
          replaceUrl: true
        }
      });

      const allDevices = (response.data.devices ?? []).map((device) => ({
        status: device.status,
        deviceId: device.device_id,
        model: device.model,
        vram: typeof device.vram === "number" ? device.vram : null,
        uptime30d:
          typeof device.uptime_30d === "number"
            ? device.uptime_30d
            : typeof device.uptime === "number"
              ? device.uptime
              : null,
        ioEarnedAllTime: device.io_earned_all_time
      }));

      const startIndex = (Math.max(1, page) - 1) * Math.max(1, limit);
      const pagedDevices = allDevices.slice(startIndex, startIndex + Math.max(1, limit));

      return {
        results: pagedDevices,
        resultCount: allDevices.length
      };
    } catch (error) {
      throw error;
    }
  },
  fetchSupplierEpochHistoryRequest: async ({ page, limit }) => {
    try {
      const response = await executeAPIRequest<SupplierEpochHistoryResponse>({
        method: "get",
        url: `${SUPPLIER_TOKENOMICS_BASE_URL}/me/epochs?page=${page}&page_size=${limit}`,
        options: {
          replaceUrl: true
        }
      });

      return {
        results: (response.data.epochs ?? []).map((epoch) => ({
          epochId: epoch.epoch_id,
          start: epoch.start,
          end: epoch.end,
          devicesActive: epoch.devices_active,
          totalPayout: epoch.total_payout,
          epochSignature: typeof epoch.epoch_signature === "string" ? epoch.epoch_signature : null
        })),
        resultCount: response.data.total ?? 0
      };
    } catch (error) {
      throw error;
    }
  }
}));
